import type { EventInput, EventRecord } from '../../shared/types';
import { API, ApiError } from './api';
import { reportClientError, reportingFetch } from './clientErrors';

type Snapshot = { input: EventInput; json: string };
export type SaveState = 'saved' | 'saving' | 'pending' | 'error';

/** Serialize saves so a slow response can never overwrite newer typing. */
export class DraftSaver {
  private latest: Snapshot | null = null;
  private savedJson: string;
  private running: Promise<void> | null = null;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private revision: number;
  private error: unknown;
  private disposed = false;
  constructor(
    readonly id: string,
    revision: number,
    input: EventInput,
    private send: (input: EventInput, version: number) => Promise<EventRecord>,
    private changed: (state: SaveState, error?: unknown) => void,
    private saved: (record: EventRecord, input: EventInput, latest: boolean) => void,
  ) {
    this.revision = revision;
    this.savedJson = JSON.stringify(input);
  }
  get version() { return this.revision; }
  change(input: EventInput) {
    const json = JSON.stringify(input);
    this.latest = { input: JSON.parse(json), json };
    clearTimeout(this.timer);
    this.error = undefined;
    if (json === this.savedJson && !this.running) { this.changed('saved'); return; }
    this.changed('pending');
    this.timer = setTimeout(() => { void this.flush().catch(() => {}); }, 450);
  }
  async flush(): Promise<void> {
    clearTimeout(this.timer);
    if (this.running) await this.running;
    if (this.latest && this.latest.json !== this.savedJson) {
      this.running = this.drain();
      try { await this.running; } finally { this.running = null; }
    }
    if (this.error) throw this.error;
  }
  private async drain() {
    this.changed('saving');
    try {
      while (this.latest && this.latest.json !== this.savedJson) {
        const snapshot = this.latest;
        const record = await this.send(snapshot.input, this.revision);
        this.revision = record.draftVersion;
        this.savedJson = snapshot.json;
        this.saved(record, snapshot.input, this.latest.json === snapshot.json);
      }
      this.error = undefined;
      this.changed('saved');
    } catch (error) {
      reportClientError('editor: save draft', error, { eventId: this.id });
      this.error = error;
      this.changed('error', error);
      // Conflicts and expired sessions require the organizer's attention.
      if (!this.disposed && (!(error instanceof ApiError) || error.status >= 500)) {
        this.timer = setTimeout(() => { void this.flush().catch(() => {}); }, 5000);
      }
      throw error;
    }
  }
  adopt(record: EventRecord, input: EventInput) {
    this.revision = record.draftVersion;
    this.savedJson = JSON.stringify(input);
    this.latest = { input, json: this.savedJson };
    this.error = undefined;
    this.changed(this.latest && this.latest.json !== this.savedJson ? 'pending' : 'saved');
  }
  dispose() {
    this.disposed = true;
    clearTimeout(this.timer);
    // Keep the final queued write alive when navigating between app pages.
    void this.flush().catch(() => {});
  }
}

export async function saveEventDraft(id: string, input: EventInput, version: number): Promise<EventRecord> {
  const body = JSON.stringify({ input, version });
  const response = await reportingFetch(`${API}/events/${id}/draft`, {
    method: 'PUT', headers: { 'content-type': 'application/json' }, credentials: 'same-origin',
    body, keepalive: new TextEncoder().encode(body).length < 60_000,
  });
  const data = await response.json();
  if (!response.ok) throw new ApiError(response.status, data.error ?? 'save_failed');
  return data as EventRecord;
}
